import { useState } from 'react';
import { FaFire, FaDumbbell, FaChartPie, FaDownload } from 'react-icons/fa';
import AdComponent from '../components/AdComponent';
import { generateDietPlanPDF } from '../utils/dietPlanPDF';

const activityMultipliers = {
  sedentary: { label: 'Sedentary (desk job, no exercise)', value: 1.2 },
  light:     { label: 'Light (1–3 days/week)', value: 1.375 },
  moderate:  { label: 'Moderate (3–5 days/week)', value: 1.55 },
  active:    { label: 'Active (6–7 days/week)', value: 1.725 },
  extra:     { label: 'Athlete / Physical Job', value: 1.9 },
};

const goalConfig = {
  cut:      { label: '🔥 Fat Loss',     delta: -500, color: 'text-red-400',    border: 'border-red-500',    bg: 'bg-red-900/30' },
  maintain: { label: '⚖️ Maintain',     delta: 0,    color: 'text-yellow-400', border: 'border-yellow-500', bg: 'bg-yellow-900/30' },
  bulk:     { label: '💪 Muscle Gain',  delta: +300, color: 'text-green-400',  border: 'border-green-500',  bg: 'bg-green-900/30' },
};

export default function CalorieCalculator() {
  const [form, setForm] = useState({ weight: '', height: '', age: '', gender: 'male', activity: 'moderate' });
  const [goal, setGoal] = useState('maintain');
  const [result, setResult] = useState(null);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const calculate = () => {
    const w = parseFloat(form.weight);
    const h = parseFloat(form.height);
    const a = parseFloat(form.age);
    if (!w || !h || !a) return;

    const bmr = form.gender === 'male'
      ? 10 * w + 6.25 * h - 5 * a + 5
      : 10 * w + 6.25 * h - 5 * a - 161;

    const tdee = bmr * activityMultipliers[form.activity].value;
    const target = tdee + goalConfig[goal].delta;

    const protein = Math.round(w * 2.2);
    const fat     = Math.round((target * 0.25) / 9);
    const carbs   = Math.round((target - protein * 4 - fat * 9) / 4);

    setResult({ bmr: Math.round(bmr), tdee: Math.round(tdee), target: Math.round(target), protein, fat, carbs });
  };

  const macroTotal = result ? result.protein * 4 + result.fat * 9 + result.carbs * 4 : 1;
  const proteinPct = result ? Math.round((result.protein * 4 / macroTotal) * 100) : 0;
  const fatPct     = result ? Math.round((result.fat * 9 / macroTotal) * 100) : 0;
  const carbsPct   = result ? Math.round((result.carbs * 4 / macroTotal) * 100) : 0;

  return (
    <div className="min-h-screen bg-black py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">

        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-5xl font-bold text-yellow-400 uppercase tracking-wide mb-3">
            Calorie Calculator
          </h1>
          <p className="text-gray-400 text-base sm:text-lg">
            TDEE · Macros · Goal-based Calorie Targets
          </p>
        </div>

        {/* Ad */}
        <div className="mb-8 flex justify-center"><AdComponent /></div>

        {/* Input Card */}
        <div className="bg-gradient-to-r from-yellow-500 via-orange-500 to-red-600 rounded-2xl p-1 mb-8">
          <div className="bg-gray-950 rounded-2xl p-6 sm:p-8">
            <h2 className="text-xl font-bold text-yellow-400 uppercase mb-6 flex items-center gap-2">
              <FaFire /> Your Stats
            </h2>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              {[
                { label: 'Weight (kg)', name: 'weight', placeholder: 'Enter Your Weight' },
                { label: 'Height (cm)', name: 'height', placeholder: 'Enter Your Height' },
                { label: 'Age',         name: 'age',    placeholder: 'Enter Your Age' },
              ].map(f => (
                <div key={f.name}>
                  <label className="block text-gray-400 text-xs uppercase mb-1">{f.label}</label>
                  <input
                    type="number" name={f.name} value={form[f.name]}
                    onChange={handleChange} placeholder={f.placeholder}
                    className="w-full px-3 py-3 bg-black border-2 border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                  />
                </div>
              ))}
              <div>
                <label className="block text-gray-400 text-xs uppercase mb-1">Gender</label>
                <select name="gender" value={form.gender} onChange={handleChange}
                  className="w-full px-3 py-3 bg-black border-2 border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-400">
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-gray-400 text-xs uppercase mb-2">Activity Level</label>
              <select name="activity" value={form.activity} onChange={handleChange}
                className="w-full px-3 py-3 bg-black border-2 border-gray-700 rounded-lg text-white focus:outline-none focus:border-orange-400">
                {Object.entries(activityMultipliers).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>

            {/* Goal Selector */}
            <div className="mb-6">
              <label className="block text-gray-400 text-xs uppercase mb-3">Your Goal</label>
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(goalConfig).map(([k, v]) => (
                  <button key={k} onClick={() => setGoal(k)}
                    className={`py-3 rounded-xl font-bold text-sm uppercase border-2 transition-all ${
                      goal === k ? `${v.bg} ${v.border} ${v.color}` : 'border-gray-700 text-gray-500 hover:border-gray-500'
                    }`}>
                    {v.label}
                  </button>
                ))}
              </div>
            </div>

            <button onClick={calculate}
              className="w-full bg-gradient-to-r from-yellow-500 via-orange-500 to-red-600 hover:opacity-90 text-black font-bold py-4 rounded-xl uppercase tracking-widest transition-all text-lg">
              Calculate Now
            </button>
          </div>
        </div>

        {/* Results */}
        {result && (
          <>
            {/* TDEE Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              {[
                { label: 'BMR',                  value: `${result.bmr}`,    unit: 'kcal', color: 'text-gray-300',         border: 'border-gray-600',         icon: <FaFire /> },
                { label: 'TDEE',                 value: `${result.tdee}`,   unit: 'kcal', color: 'text-orange-400',        border: 'border-orange-500',        icon: <FaFire /> },
                { label: goalConfig[goal].label, value: `${result.target}`, unit: 'kcal', color: goalConfig[goal].color,   border: goalConfig[goal].border,    icon: <FaDumbbell /> },
              ].map((c, i) => (
                <div key={i} className={`bg-gray-900 border-2 ${c.border} rounded-xl p-4 text-center`}>
                  <div className={`text-2xl mb-1 ${c.color} flex justify-center`}>{c.icon}</div>
                  <div className={`text-2xl sm:text-3xl font-bold ${c.color}`}>{c.value}</div>
                  <div className="text-gray-500 text-xs">{c.unit}</div>
                  <div className="text-gray-400 text-xs uppercase mt-1">{c.label}</div>
                </div>
              ))}
            </div>

            {/* Macro Split */}
            <div className="bg-gray-900 border-2 border-yellow-500/40 rounded-2xl p-6 mb-8">
              <h3 className="text-lg font-bold text-yellow-400 uppercase mb-5 flex items-center gap-2">
                <FaChartPie /> Daily Macro Split
              </h3>
              <div className="grid grid-cols-3 gap-4 mb-5">
                {[
                  { label: 'Protein', value: result.protein, unit: 'g', pct: proteinPct, color: 'bg-yellow-500',  text: 'text-yellow-400' },
                  { label: 'Carbs',   value: result.carbs,   unit: 'g', pct: carbsPct,   color: 'bg-orange-500', text: 'text-orange-400' },
                  { label: 'Fat',     value: result.fat,     unit: 'g', pct: fatPct,     color: 'bg-red-500',    text: 'text-red-400' },
                ].map((m, i) => (
                  <div key={i} className="text-center">
                    <div className={`text-3xl font-bold ${m.text}`}>{m.value}<span className="text-sm ml-1">{m.unit}</span></div>
                    <div className="text-gray-500 text-xs uppercase mb-2">{m.label}</div>
                    <div className="w-full bg-gray-800 rounded-full h-2">
                      <div className={`${m.color} h-2 rounded-full transition-all duration-700`} style={{ width: `${m.pct}%` }}></div>
                    </div>
                    <div className="text-gray-500 text-xs mt-1">{m.pct}%</div>
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs text-center">Based on 2.2g protein/kg · 25% fat · remaining carbs</p>
            </div>

            {/* Ad between sections */}
            <div className="mb-8 flex justify-center"><AdComponent /></div>

            {/* Free Diet Plan PDF Download */}
            <div className="bg-gradient-to-r from-yellow-500 via-orange-500 to-red-600 rounded-2xl p-1 mb-8">
              <div className="bg-gray-950 rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6">
                <div className="flex-1">
                  <div className="text-yellow-400 font-bold text-xl uppercase mb-1">📄 Free Diet Plan PDF</div>
                  <p className="text-gray-400 text-sm">
                    Get your personalized <span className="text-orange-400 font-semibold">{goalConfig[goal].label}</span> meal plan — full day meals, macros, and pro tips from Coach Sabari. 100% free.
                  </p>
                </div>
                <button
                  onClick={() => generateDietPlanPDF(result, form, goal)}
                  className="flex items-center gap-3 bg-gradient-to-r from-yellow-500 to-orange-600 hover:opacity-90 text-black font-bold px-8 py-4 rounded-xl uppercase tracking-wide transition-all transform hover:scale-105 whitespace-nowrap"
                >
                  <FaDownload size={18} />
                  Download PDF
                </button>
              </div>
            </div>
          </>
        )}

        {/* Bottom Ad */}
        <div className="flex justify-center"><AdComponent /></div>
      </div>
    </div>
  );
}
